import { useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { colors } from '../../utils/colors';
import { getDocumentFoldersAPI, getFolderDocumentsAPI } from '../../lib/api';
import LoadingProgress from '../../components/common/LoadingProgress';
import FolderList from './documents/FolderList';
import FolderDetails from './documents/FolderDetails';

const Documents = ({ customerId, projectId, onBack }) => {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFolders();
  }, [customerId, projectId]);

  const fetchFolders = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getDocumentFoldersAPI(customerId, projectId);
      if (result.status && result.data) {
        setFolders(Array.isArray(result.data) ? result.data : []);
      } else {
        setError('Failed to load document folders');
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch document folders');
      console.error('Error fetching document folders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFolderDocuments = async (folderId) => {
    try {
      setDocumentsLoading(true);
      const result = await getFolderDocumentsAPI(customerId, projectId, folderId);
      if (result.status && result.data) {
        setDocuments(Array.isArray(result.data) ? result.data : []);
      }
    } catch (error) {
      console.error('Error fetching folder documents:', error);
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleFolderClick = (folder) => {
    setSelectedFolder(folder);
    fetchFolderDocuments(folder.id);
  };

  const handleBackToFolders = () => {
    setSelectedFolder(null);
    setDocuments([]);
  };

  if (loading) {
    return <LoadingProgress loading={loading} />;
  }

  if (selectedFolder) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, bgcolor: '#ffffff', minHeight: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2.5, sm: 3 } }}>
          <IconButton
            onClick={handleBackToFolders}
            sx={{
              color: colors.primary.main,
              bgcolor: colors.background.hover,
              '&:hover': { bgcolor: colors.primary.light + '20' },
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </IconButton>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: colors.text.primary,
              fontSize: { xs: '1.375rem', sm: '1.625rem', md: '1.875rem', lg: '2rem' }
            }}
          >
            {selectedFolder.name}
          </Typography>
        </Box>

        <FolderDetails
          documents={documents}
          loading={documentsLoading}
          customerId={customerId}
          projectId={projectId}
          folderId={selectedFolder.id}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, bgcolor: '#ffffff', minHeight: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2.5, sm: 3 } }}>
        {onBack && (
          <IconButton
            onClick={onBack}
            sx={{
              color: colors.primary.main,
              bgcolor: colors.background.hover,
              '&:hover': { bgcolor: colors.primary.light + '20' },
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 }
            }}
          >
            <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </IconButton>
        )}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: colors.text.primary,
            fontSize: { xs: '1.375rem', sm: '1.625rem', md: '1.875rem', lg: '2rem' }
          }}
        >
          Documents
        </Typography>
      </Box>

      <FolderList
        folders={folders}
        onFolderClick={handleFolderClick}
        error={error}
        customerId={customerId}
        projectId={projectId}
        onFolderCreated={fetchFolders}
      />
    </Box>
  );
};

export default Documents;
